import { Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { AdminRole } from './core/auth/models/admin-user-info';
import { roleGuard } from './core/auth/role.guard';
import { countryFeatureGuard } from './core/country/country-feature.guard';

export const routes: Routes = [
    {
        path: 'login',
        loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
    },
    {
        path: '',
        canActivate: [authGuard],
        children: [
            {
                path: '',
                redirectTo: 'dashboard',
                pathMatch: 'full',
            },
            {
                path: 'dashboard',
                loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
            },
            {
                path: 'customers',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/customers/customers.routes'),
            },
            {
                path: 'subscriptions',
                loadChildren: () => import('./pages/subscriptions/subscriptions.routes'),
            },
            {
                path: 'payments',
                loadChildren: () => import('./pages/payments/payments.routes'),
            },
            {
                path: 'tariffs',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/tariffs/tariffs.routes'),
            },
            {
                path: 'promos',
                loadChildren: () => import('./pages/promos/promos.routes'),
            },
            {
                path: 'bonuses',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/bonuses/bonuses.routes'),
            },
            {
                path: 'disputes',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/disputes/disputes.routes'),
            },
            {
                path: 'users',
                canActivate: [roleGuard(AdminRole.Admin)],
                loadChildren: () => import('./pages/users/users.routes'),
            },
            {
                path: 'scheduler',
                canActivate: [roleGuard(AdminRole.Admin)],
                loadChildren: () => import('./pages/scheduler/scheduler.routes'),
            },
            {
                path: 'notifications',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/notifications/notifications.routes'),
            },
            {
                path: 'geo',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/geo/geo.routes'),
            },
            {
                path: 'blacklists',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/blacklists/blacklists.routes'),
            },
            {
                path: 'referrals',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/referrals/referrals.routes'),
            },
            {
                path: 'unmanaged-ordering',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator), countryFeatureGuard('unmanagedOrdering')],
                loadChildren: () => import('./pages/unmanaged-ordering/unmanaged-ordering.routes'),
            },
            {
                path: 'settings',
                canActivate: [roleGuard(AdminRole.Admin)],
                loadChildren: () => import('./pages/settings/settings.routes'),
            },
            {
                path: 'seo',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/seo/seo.routes'),
            },
            {
                path: 'localization',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator, AdminRole.Translator)],
                loadChildren: () => import('./pages/localization/localization.routes'),
            },
            {
                path: 'support-knowledge',
                loadChildren: () => import('./pages/support-knowledge/support-knowledge.routes'),
            },
            {
                path: 'ai-conversations',
                loadChildren: () => import('./pages/ai-conversations/ai-conversations.routes'),
            },
            {
                path: 'chats',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/chats/chats.routes'),
            },
            {
                path: 'ai-search-requests',
                loadChildren: () => import('./pages/ai-search-requests/ai-search-requests.routes'),
            },
            {
                path: 'ai-search-analytics',
                loadChildren: () => import('./pages/ai-search-analytics/ai-search-analytics.routes'),
            },
            {
                path: 'delivery-logs',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/delivery-logs/delivery-logs.routes'),
            },
            {
                path: 'reports',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/reports/reports.routes'),
            },
            {
                path: 'background-checks',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator), countryFeatureGuard('backgroundChecks')],
                loadChildren: () => import('./pages/background-checks/background-checks.routes'),
            },
            {
                path: 'phone-verifications',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/phone-verifications/phone-verifications.routes'),
            },
            {
                path: 'customer-complaints',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/customer-complaints/customer-complaints.routes'),
            },
            {
                path: 'documents',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/documents/documents.routes'),
            },
            {
                path: 'photo-moderation',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/photo-moderation/photo-moderation.routes'),
            },
            {
                path: 'audio-message-moderation',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/audio-message-moderation/audio-message-moderation.routes'),
            },
            {
                path: 'resume-moderation',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/resume-moderation/resume-moderation.routes'),
            },
            {
                path: 'vacancy-moderation',
                canActivate: [roleGuard(AdminRole.Admin, AdminRole.Moderator)],
                loadChildren: () => import('./pages/vacancy-moderation/vacancy-moderation.routes'),
            },
        ],
    },
    {
        path: '**',
        redirectTo: '',
    },
];
